/**
 * relation-graph-modal.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component } from "@angular/core";
import { FormControl } from "@angular/forms";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import * as D3Force from "d3-force";

import { Observable } from "rxjs/Rx";
import { RelationEdge, RelationGraph, RelationNode } from "./relation-graph";
import { RelationshipType, Role } from "./relationship";
import { Subject } from "./subject";
import { SubjectService } from "./subject.service";

/**
 * A context containing a {@link RelationGraph} to be displayed.
 */
export class RelationGraphContext extends BSModalContext
{
  public graph : RelationGraph;
  public root? : Subject;
}

/**
 * The {@link RelationGraphModalComponent} presents the "relationship graph" of
 * a {@link Subject}'s relationships, as modeled by the {@link RelationGraph}
 * and displayed by a {@link ForceDirectedGraphComponent}.
 */
@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      Relationship Network Graph
    </h4>
  </div>

  <div class="modal-body">
    <div class="row">
      <div class="col-sm-4">
        <p>
          Subjects and their relationships can be filtered using these controls.
          Subjects with no matching relationships will be removed from the graph
          and displayed in gray in the table below.
        </p>
        <p>
          Filters can be removed by selecting the blank entry in each list.
        </p>
      </div>
      <div class="col-sm-4">
        <label>Filter by relationship type:</label>
        <select class="form-control" [formControl]="typeFilter">
          <option [ngValue]="null"></option>
          <option
            *ngFor="let type of subjects.relationshipTypes"
            [ngValue]="type">
            {{type.displayName}}
          </option>
        </select>
      </div>
      <div class="col-sm-4">
        <label>Filter by relationships with role:</label>
        <select class="form-control" [formControl]="roleFilter">
          <option [ngValue]="null"></option>
          <option
            *ngFor="let role of subjects.roles"
            [ngValue]="role">
            {{role.displayName}}
          </option>
        </select>
      </div>
    </div>
  </div>

  <table
    *ngFor="let type of graph.knownNodeCategories; let firstType = first"
    class="table center-items text-center">
    <caption
      *ngIf="graph.knownNodeCategories.length > 1"
      class="capitalized">
      <!-- (only shown if there are multiple node types) -->
      {{type}}
    </caption>
    <thead *ngIf="firstType">
      <th class="text-center">ID</th>
      <template [ngIf]="graph?.nodes?.length > 0">
        <th
          *ngFor="let col of graph.nodes[0].summary"
          class="capitalized text-center">
          {{col.displayName || col.name}}
        </th>
      </template>
      <th class="text-center">Actions</th>
    </thead>
    <tbody>
      <tr
        *ngFor="let node of graph?.nodes"
        [class.highlight]="highlight == node.id"
        [class.disabled]="!node.enabled"
        (mouseenter)="highlight = node.id"
        (mouseleave)="highlight = null">
        <template [ngIf]="node.category === type">
          <td>{{node.id}}</td>
          <td *ngFor="let summaryField of node.summary">
            <typed-display [input]="summaryField"></typed-display>
          </td>
          <td>
            <button
              class="btn btn-default btn-xs"
              width="50%"
              (click)="expand(node)"
              [disabled]="node.alreadyExpanded">
              Expand
            </button>
            <button
              class="btn btn-primary btn-xs"
              width="50%"
              (click)="open(node)">
              Open
            </button>
          </td>
        </template>
      </tr>
      <tr *ngIf="graph?.nodes?.length === 0">
        <td colspan="3" class="text-center text-danger">
          No subjects found in network!
        </td>
      </tr>
      <tr *ngIf="!graph">
        <td colspan="3" class="text-center">
          <spinner></spinner>
        </td>
      </tr>
    </tbody>
  </table>

  <force-directed-graph
    [graph]="graph"
    [highlight]="highlight"
    [width]="800"
    [height]="400"
    (selected)="highlight = $event"
    (expand)="expand($event)"
    (simulation)="simulation = $event"
    [coolingRate]="0.005">
  </force-directed-graph>

  <p class="text-center">
    Nodes with a dotted ring may have additional relationships. Either click on
    the node itself, or the corresponding "expand" button to search.
  </p>

  <div class="modal-footer">
    <button
      type="button"
      class="btn btn-primary"
      (click)="dialog.close()">
      OK
    </button>
  </div>
  `,
  styles: [`
    table {
      width: 100%;
    }
    badge {
      margin: 15px;
    }
    /deep/ table td div p:last-child {
      margin-bottom: 0;
    }
    table .special-message td {
      text-align: center;
    }
    table caption {
      text-align: left;
      padding-left: 2em;
      font-style: italic;
    }
    .fit-to-modal td:first-child, .fit-to-modal th:first-child {
      padding-left: 30px;
    }
    .fit-to-modal td:last-child, .fit-to-modal th:last-child {
      padding-right: 30px;
    }
    .table.fit-to-modal {
      margin-bottom: 0;
    }
    .table tbody {border-bottom: 1px solid #ddd;}
    .table tr.highlight {background-color: lightyellow;}
    .table tr td {padding: 3px;}
    tr.disabled {
      background: #eee !important;
      color: #888 !important;
    }
    `]
})
export class RelationGraphModalComponent
  implements ModalComponent<RelationGraphContext>
{
  /**
   * The node ID to be highlighted to demonstrate the pairing between nodes and
   * data rows in the table. This is a virtual 2-way binding:
   *  - When the user hovers over a table row, the value is set (here) and then
   *    propagated in to the force-directed graph.
   *  - When the user hovers over a graph node, the graph component emits an
   *    event via (selected), which then sets this value.
   */
  private highlight : number | null = null;

  /**
   * The {@link Graph} to be displayed. We specify the {@link RelationGraph}
   * subtype, so we can access relation-specific properties.
   */
  private graph : RelationGraph;

  /**
   * Hook into the {@link ForceDirectedGraphComponent}'s simulation, currently
   * used solely for debugging purposes.
   */
  private simulation : D3Force.Simulation<any, any>;

  /**
   * Construct the {@link RelationGraphModalComponent}
   * @param  {DialogRef<RelationGraphContext>} dialog
   * @return {ReportModalComponent}
   */
  constructor (
    public dialog : DialogRef<RelationGraphContext>,
    public subjects : SubjectService
  )
  {
    console.warn(dialog);
    this.graph = dialog.context.graph;
    this.typeFilter.valueChanges.subscribe(
      (value : RelationshipType) => this.filter());
    this.roleFilter.valueChanges.subscribe(
      (value : RelationshipType) => this.filter());
  }

  /**
   * Attempt to open a node, by passing it along to the service that opened this
   * dialog via the modal dialog's close interface.
   * @param node The graph node to open
   */
  public open (node : RelationNode) : void
  {
    this.dialog.close(node);
  }

  /**
   * Expand / explore the links of a particular node. This requires the graph's
   * expand function, which is basically just a hole through to the same method
   * on {@link SubjectService}.
   * @param node The center of desired expansion
   */
  public expand (node : RelationNode) : void
  {
    if (!node.alreadyExpanded)
    {
      this.graph.expand(node);
    }
  }

  public typeFilter : FormControl = new FormControl(null);
  public roleFilter : FormControl = new FormControl(null);

  protected enableNodesByEdgePossession () : void
  {
    this.graph.nodes.forEach((n : RelationNode) => {
      n.simulated = false;
      n.enabled = false;
    });
    this.graph.edges.forEach((e : RelationEdge) => {
      if (e.enabled)
      {
        e.source.enabled = true;
        e.target.enabled = true;
      }
    });
  }

  protected enableAndDesim (x : RelationEdge | RelationNode) : void
  {
    x.simulated = false;
    x.enabled = true;
  }

  public filter () : void
  {
    const relType : RelationshipType = this.typeFilter.value;
    const roleType : Role = this.roleFilter.value;
    const clear : boolean = (!relType && !roleType);
    if (clear)
    {
      this.graph.edges.forEach((e : RelationEdge) => this.enableAndDesim(e));
      this.graph.nodes.forEach((n : RelationNode) => this.enableAndDesim(n));
    }
    else
    {
      // this is for relationship types
      this.graph.edges.forEach((e : RelationEdge) => {
        e.simulated = false;
        e.enableAll();
        if (relType)
        {
          e.filterByType(relType.name);
        }
        if (roleType)
        {
          e.filterByRole(roleType.name);
        }
      });
      this.enableNodesByEdgePossession();
    }
    this.graph = Object.assign(new RelationGraph(), this.graph);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/relation-graph-modal.component.ts